import { Helmet } from 'react-helmet-async';

export default function CookiesPage() {
  const cookieTypes = [
    {
      name: 'Essential Cookies',
      description: 'Required for the website to function properly. These cannot be disabled.',
      examples: ['Session management', 'Shopping cart', 'Security tokens'],
      required: true
    },
    {
      name: 'Functional Cookies',
      description: 'Enable enhanced functionality and personalization.',
      examples: ['Language preferences', 'Region settings', 'Saved searches'],
      required: false
    },
    {
      name: 'Analytics Cookies',
      description: 'Help us understand how visitors interact with our website.',
      examples: ['Page views', 'Traffic sources', 'User journeys'],
      required: false
    },
    {
      name: 'Marketing Cookies',
      description: 'Used to deliver relevant advertisements and track campaign effectiveness.',
      examples: ['Ad personalization', 'Retargeting', 'Conversion tracking'],
      required: false
    }
  ];

  return (
    <>
      <Helmet><title>Cookie Policy | BESTIX</title></Helmet>
      <div className="min-h-screen bg-gray-50">
        <div className="bg-navy py-16">
          <div className="container mx-auto px-4 text-center">
            <h1 className="font-playfair text-4xl font-bold !text-white">Cookie Policy</h1>
            <p className="mt-4 text-gray-300">Last updated: January 2026</p>
          </div>
        </div>

        <div className="container mx-auto px-4 py-16">
          <div className="max-w-3xl mx-auto">
            
            {/* Introduction */}
            <section className="mb-12">
              <h2 className="text-2xl font-semibold text-navy mb-4">What Are Cookies?</h2>
              <p className="text-gray-600 mb-4">
                Cookies are small text files stored on your device when you visit websites. They help websites remember your preferences, understand how you use the site, and improve your experience.
              </p>
              <p className="text-gray-600">
                BESTIX uses cookies and similar technologies to provide, protect, and improve our services.
              </p>
            </section>

            {/* Cookie Types */}
            <section className="mb-12">
              <h2 className="text-2xl font-semibold text-navy mb-6">Types of Cookies We Use</h2>
              <div className="space-y-6">
                {cookieTypes.map((cookie, index) => (
                  <div key={index} className="bg-white rounded-xl shadow-md p-6">
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="font-semibold text-navy text-lg">{cookie.name}</h3>
                      {cookie.required ? (
                        <span className="text-xs bg-navy text-white px-2 py-1 rounded">Required</span>
                      ) : (
                        <span className="text-xs bg-gray-200 text-gray-600 px-2 py-1 rounded">Optional</span>
                      )}
                    </div>
                    <p className="text-gray-600 mb-3">{cookie.description}</p>
                    <div className="flex flex-wrap gap-2">
                      {cookie.examples.map((example, i) => (
                        <span key={i} className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded">
                          {example}
                        </span>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </section>

            {/* Third-Party Cookies */}
            <section className="mb-12">
              <h2 className="text-2xl font-semibold text-navy mb-4">Third-Party Cookies</h2>
              <p className="text-gray-600 mb-4">
                We work with trusted partners who may also place cookies on your device. These include:
              </p>
              <ul className="list-disc pl-6 text-gray-600 space-y-2">
                <li><strong>Google Analytics:</strong> Website analytics and performance measurement</li>
                <li><strong>Stripe:</strong> Payment processing and fraud prevention</li>
                <li><strong>Intercom:</strong> Customer support chat functionality</li>
                <li><strong>Facebook/Meta:</strong> Social features and advertising</li>
              </ul>
              <p className="text-gray-600 mt-4">
                These third parties have their own privacy policies governing their use of cookies.
              </p>
            </section>

            {/* Managing Cookies */}
            <section className="mb-12">
              <h2 className="text-2xl font-semibold text-navy mb-4">Managing Your Cookie Preferences</h2>
              <div className="bg-white rounded-xl shadow-md p-6">
                <p className="text-gray-600 mb-4">
                  You can control cookies through several methods:
                </p>
                <div className="space-y-4">
                  <div className="flex gap-3">
                    <div className="flex-shrink-0 w-6 h-6 bg-gold/20 rounded flex items-center justify-center">
                      <svg className="w-4 h-4 text-gold" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                    <div>
                      <h4 className="font-medium text-navy">Browser Settings</h4>
                      <p className="text-sm text-gray-600">Most browsers allow you to block or delete cookies through settings.</p>
                    </div>
                  </div>
                  <div className="flex gap-3">
                    <div className="flex-shrink-0 w-6 h-6 bg-gold/20 rounded flex items-center justify-center">
                      <svg className="w-4 h-4 text-gold" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                    <div>
                      <h4 className="font-medium text-navy">Opt-Out Tools</h4>
                      <p className="text-sm text-gray-600">Use industry opt-out tools like NAI or DAA to manage ad tracking.</p>
                    </div>
                  </div>
                  <div className="flex gap-3">
                    <div className="flex-shrink-0 w-6 h-6 bg-gold/20 rounded flex items-center justify-center">
                      <svg className="w-4 h-4 text-gold" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                    <div>
                      <h4 className="font-medium text-navy">Cookie Banner</h4>
                      <p className="text-sm text-gray-600">Use our cookie consent banner to manage preferences on this site.</p>
                    </div>
                  </div>
                </div>
                <p className="text-sm text-gray-500 mt-4">
                  Note: Disabling certain cookies may affect website functionality.
                </p>
              </div>
            </section>

            {/* Data Retention */}
            <section className="mb-12">
              <h2 className="text-2xl font-semibold text-navy mb-4">Data Retention</h2>
              <p className="text-gray-600">
                Cookie lifespans vary by type. Session cookies are deleted when you close your browser. Persistent cookies remain for a set period (typically 30 days to 2 years) unless you delete them sooner.
              </p>
            </section>

            {/* Contact */}
            <section>
              <h2 className="text-2xl font-semibold text-navy mb-4">Questions?</h2>
              <p className="text-gray-600">
                If you have questions about our use of cookies, please contact us at:
              </p>
              <p className="text-navy font-semibold mt-4">
                privacy@bestix.ai
              </p>
            </section>

          </div>
        </div>
      </div>
    </>
  );
}
